import { router } from "@inertiajs/react"
import { Folder, ImageOff, Loader2, Tag } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { ClipActionsMenu } from "./ClipActionsMenu"
import { ClipStatusBadge } from "./ClipStatusBadge"
import { ClipTagPills } from "./ClipTagPills"
import { OneTimeUseBadge } from "./OneTimeUseBadge"
import { useThumbnailHoverCycle } from "./useThumbnailHoverCycle"
import type { Clip } from "./types"

const DEPRECATION_CANDIDATE_DAYS = 90

export function ClipListRow({
  clip,
  selected,
  pendingThumbnail,
  onSelectChange,
  onRequestThumbnails,
}: {
  clip: Clip
  selected: boolean
  pendingThumbnail?: boolean
  onSelectChange: (clipId: number, selected: boolean) => void
  onRequestThumbnails?: (ids: number[]) => void
}) {
  const { ref, index } = useThumbnailHoverCycle<HTMLTableRowElement>(clip.thumbnails)
  const displayedThumb = clip.thumbnails[index] ?? clip.thumbnails[0]
  const thumbUrl = displayedThumb?.url ?? clip.primary_thumbnail_url
  const isDeprecationCandidate =
    clip.status !== "deprecated" && isAtLeastNDaysOld(clip.recorded_date, DEPRECATION_CANDIDATE_DAYS)

  function navigateToClip() {
    router.visit(`/clips/${clip.id}`)
  }

  function handleDeprecate(e: React.MouseEvent) {
    e.stopPropagation()
    router.patch(`/clips/${clip.id}/deprecate`, {}, { preserveScroll: true })
  }

  return (
    <tr
      ref={ref}
      onClick={navigateToClip}
      className={cn(
        "cursor-pointer border-b border-hairline transition-colors hover:bg-surface",
        selected && "bg-accent-faded/40 hover:bg-accent-faded/40",
      )}
    >
      <td className="w-8 px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onChange={(e) => onSelectChange(clip.id, e.target.checked)}
          aria-label={`Select ${clip.title ?? clip.current_filename}`}
        />
      </td>
      <td className="w-32 px-2 py-3">
        <div className="aspect-video w-32 overflow-hidden rounded-md border border-hairline bg-page">
          {pendingThumbnail && !thumbUrl ? (
            <div className="flex h-full w-full items-center justify-center text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : thumbUrl ? (
            <img src={thumbUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-ink-muted">
              <ImageOff className="h-4 w-4" />
            </div>
          )}
        </div>
      </td>
      <td className="min-w-[200px] px-2 py-3">
        <span
          className="block truncate font-medium text-ink-display"
          title={clip.title ?? clip.current_filename ?? ""}
        >
          {clip.title || clip.current_filename || "Untitled"}
        </span>
        {clip.short_description && (
          <p className="truncate text-xs text-ink-muted">{clip.short_description}</p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-muted">
          <span className="inline-flex items-center gap-1.5" title={clip.current_folder_path ?? ""}>
            <Folder className="h-3 w-3 shrink-0" />
            <span className="truncate">{clip.current_folder_path || "/"}</span>
          </span>
          {clip.tag_names.length > 0 && (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <Tag className="h-3 w-3 shrink-0" />
              <ClipTagPills tags={clip.tag_names} />
            </span>
          )}
        </div>
      </td>
      <td className="py-3 pl-6 pr-2 text-xs text-ink-muted">{formatDuration(clip.duration_seconds)}</td>
      <td className="py-3 pl-6 pr-2 text-xs">
        <span className={cn(isDeprecationCandidate ? "text-danger-display font-medium" : "text-ink-muted")}>
          {clip.recorded_date ?? "—"}
        </span>
        {isDeprecationCandidate && (
          <button
            type="button"
            onClick={handleDeprecate}
            className="mt-0.5 block cursor-pointer text-[10px] text-danger-display underline-offset-2 hover:underline"
          >
            Deprecate
          </button>
        )}
      </td>
      <td className="py-3 pl-6 pr-2">
        <div className="flex flex-wrap items-center gap-1">
          <ClipStatusBadge status={clip.status} />
          {clip.one_time_use && <OneTimeUseBadge />}
        </div>
        {clip.status === "needs_processing" && onRequestThumbnails && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRequestThumbnails([clip.id]) }}
            className="mt-1 block cursor-pointer text-[10px] text-accent-display underline-offset-2 hover:underline"
          >
            Get thumbnail &amp; enrich
          </button>
        )}
      </td>
      <td className="w-10 px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <ClipActionsMenu
          clip={clip}
          onRequestThumbnails={onRequestThumbnails ? (id) => onRequestThumbnails([id]) : undefined}
        />
      </td>
    </tr>
  )
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

function isAtLeastNDaysOld(dateStr: string | null, days: number): boolean {
  if (!dateStr) return false
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return false
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  return d.getTime() <= cutoff
}
